// FINAL CHAT V2 -- turns one upstream figure spec (design A15's closed
// vocabulary) into a drawing model: plain SVG primitives in a fixed viewBox,
// a sign table, or a book image. Pure functions, no DOM -- TutorFigure.vue
// only draws what these return. Mirrors the reference console's Figure.tsx
// (mafs + chart.js there; plain SVG here, drawn in V2's palette). A spec this
// cannot draw throws, and the figure falls back to its alt text.
import {
  toFunction,
  roots,
  extrema,
  intersections,
  round,
  signTable,
  formatNumber,
} from './figureMath'

// V2 tokens: indigo-600, teal-500, warning, error, indigo-900, teal-700,
// indigo-300, muted -- then a few more for long chart series.
export const FIGURE_PALETTE = [
  '#314bd6',
  '#14b7d7',
  '#b76e00',
  '#d14545',
  '#172b85',
  '#087d9e',
  '#91a5ff',
  '#65718d',
  '#4668f0',
  '#e0a100',
  '#0b163f',
  '#cdf1f8',
]
const INK = '#0b163f'
const MUTED = '#65718d'
const GRID = '#e2e8f0'
const RED = '#d14545'
const TEAL = '#087d9e'
const AMBER = '#b76e00'
const DEEP = '#172b85'

// The drawing's width in px: the figure's own width on the page (so labels
// keep their real size on a phone), 560 at most. Set per layoutFigure call.
const MAX_WIDTH = 560
const MIN_WIDTH = 260
let WIDTH = MAX_WIDTH
const FONT = 14
const MAX_SAMPLES = 400

function finite(value) {
  return typeof value === 'number' && Number.isFinite(value)
}

function pair(value, fallback) {
  return Array.isArray(value) &&
    value.length === 2 &&
    finite(value[0]) &&
    finite(value[1]) &&
    value[0] < value[1]
    ? value
    : fallback
}

function text(x, y, value, options = {}) {
  return {
    t: 'text',
    x,
    y,
    text: String(value),
    anchor: options.anchor || 'middle',
    baseline: options.baseline || 'middle',
    size: options.size || FONT,
    fill: options.fill || INK,
    weight: options.weight || 400,
  }
}

// A step that gives about `target` round-numbered ticks over `span`.
export function niceStep(span, target = 8) {
  const raw = span / target
  if (!(raw > 0) || !Number.isFinite(raw)) return 1
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const norm = raw / magnitude
  const nice = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10
  return nice * magnitude
}

function ticks(min, max, target = 8) {
  const step = niceStep(max - min, target)
  const out = []
  for (
    let value = Math.ceil(min / step) * step;
    value <= max + step * 1e-9;
    value += step
  ) {
    out.push(round(value, 6))
    if (out.length > 60) break
  }
  return out
}

function autoRange(fns, domain) {
  let min = 0
  let max = 0
  fns.forEach(fn => {
    for (let i = 0; i <= 100; i += 1) {
      const y = fn(domain[0] + ((domain[1] - domain[0]) * i) / 100)
      if (Number.isFinite(y)) {
        min = Math.min(min, y)
        max = Math.max(max, y)
      }
    }
  })
  const pad = Math.max(1, (max - min) * 0.15)
  return [Math.max(min - pad, -1000), Math.min(max + pad, 1000)]
}

// A cartesian frame: data -> viewBox coordinates, light grid, the two axes
// (at zero when zero is in view, otherwise along the edge) and tick labels.
function cartesian(xRange, yRange, height, fmt, options = {}) {
  const pad = { left: 34, right: 16, top: 14, bottom: 26 }
  const plotW = WIDTH - pad.left - pad.right
  const plotH = height - pad.top - pad.bottom
  const sx = x => pad.left + ((x - xRange[0]) / (xRange[1] - xRange[0])) * plotW
  const sy = y =>
    pad.top + plotH - ((y - yRange[0]) / (yRange[1] - yRange[0])) * plotH
  const items = []
  // About one labelled tick per 55px across and 45px down, so a narrow
  // (phone) drawing is not crowded.
  const xTicks = ticks(
    xRange[0],
    xRange[1],
    Math.max(3, Math.min(8, Math.floor(plotW / 55)))
  )
  const yTicks =
    options.yAxis === false
      ? []
      : ticks(
          yRange[0],
          yRange[1],
          Math.max(3, Math.min(6, Math.floor(plotH / 45)))
        )
  xTicks.forEach(x =>
    items.push({
      t: 'line',
      x1: sx(x),
      y1: pad.top,
      x2: sx(x),
      y2: pad.top + plotH,
      stroke: GRID,
      width: 1,
    })
  )
  yTicks.forEach(y =>
    items.push({
      t: 'line',
      x1: pad.left,
      y1: sy(y),
      x2: pad.left + plotW,
      y2: sy(y),
      stroke: GRID,
      width: 1,
    })
  )
  const axisY = yRange[0] <= 0 && yRange[1] >= 0 ? sy(0) : sy(yRange[0])
  const axisX = xRange[0] <= 0 && xRange[1] >= 0 ? sx(0) : sx(xRange[0])
  items.push({
    t: 'line',
    x1: pad.left,
    y1: axisY,
    x2: pad.left + plotW,
    y2: axisY,
    stroke: MUTED,
    width: 1.4,
  })
  if (options.yAxis !== false) {
    items.push({
      t: 'line',
      x1: axisX,
      y1: pad.top,
      x2: axisX,
      y2: pad.top + plotH,
      stroke: MUTED,
      width: 1.4,
    })
  }
  xTicks.forEach(x => {
    if (x === 0 && options.yAxis !== false) return
    items.push(text(sx(x), axisY + 13, fmt(x), { size: 11, fill: MUTED }))
  })
  yTicks.forEach(y => {
    if (y === 0) return
    items.push(
      text(axisX - 5, sy(y), fmt(y), { size: 11, fill: MUTED, anchor: 'end' })
    )
  })
  return { sx, sy, items, pad, plotW, plotH, axisY }
}

// A function's polyline, broken wherever it is undefined or leaves the view
// by a wide margin (asymptotes).
function curvePath(fn, domain, yRange, sx, sy) {
  const span = yRange[1] - yRange[0]
  const lo = yRange[0] - span * 2
  const hi = yRange[1] + span * 2
  let d = ''
  let open = false
  for (let i = 0; i <= MAX_SAMPLES; i += 1) {
    const x = domain[0] + ((domain[1] - domain[0]) * i) / MAX_SAMPLES
    const y = fn(x)
    if (!Number.isFinite(y) || y < lo || y > hi) {
      open = false
      continue
    }
    d += `${open ? 'L' : 'M'}${sx(x).toFixed(1)} ${sy(y).toFixed(1)}`
    open = true
  }
  return d
}

// Where `fn` has the requested sign: the region between it and y = 0.
function shadePaths(fn, domain, yRange, sign, sx, sy) {
  const paths = []
  let run = []
  const clamp = y => Math.min(Math.max(y, yRange[0]), yRange[1])
  const flush = () => {
    if (run.length > 1) {
      const top = run.map(
        (p, i) =>
          `${i ? 'L' : 'M'}${sx(p.x).toFixed(1)} ${sy(clamp(p.y)).toFixed(1)}`
      )
      const last = run[run.length - 1]
      const first = run[0]
      paths.push(
        `${top.join('')}L${sx(last.x).toFixed(1)} ${sy(clamp(0)).toFixed(
          1
        )}L${sx(first.x).toFixed(1)} ${sy(clamp(0)).toFixed(1)}Z`
      )
    }
    run = []
  }
  for (let i = 0; i <= MAX_SAMPLES; i += 1) {
    const x = domain[0] + ((domain[1] - domain[0]) * i) / MAX_SAMPLES
    const y = fn(x)
    if (Number.isFinite(y) && (sign === 'positive' ? y > 0 : y < 0)) {
      run.push({ x, y })
    } else {
      flush()
    }
  }
  flush()
  return paths
}

function functionPlot(spec, fmt) {
  if (!Array.isArray(spec.functions) || !spec.functions.length) {
    throw new Error('no functions')
  }
  const domain = pair(spec.domain, [-10, 10])
  const fns = spec.functions.slice(0, 4).map(f => toFunction(f.expression))
  const range = pair(spec.range, autoRange(fns, domain))
  const height = Math.round(Math.min(300, Math.max(230, WIDTH * 0.8)))
  const frame = cartesian(domain, range, height, fmt)
  const { sx, sy } = frame
  const items = frame.items
  const inView = (x, y) =>
    x >= domain[0] && x <= domain[1] && y >= range[0] && y <= range[1]
  const marks = new Set(Array.isArray(spec.marks) ? spec.marks : [])
  const first = fns[0]

  if (spec.shade === 'positive' || spec.shade === 'negative') {
    shadePaths(first, domain, range, spec.shade, sx, sy).forEach(d =>
      items.push({
        t: 'path',
        d,
        stroke: 'none',
        fill: FIGURE_PALETTE[0],
        fillOpacity: 0.14,
      })
    )
  }
  fns.forEach((fn, i) => {
    const d = curvePath(fn, domain, range, sx, sy)
    if (d) {
      items.push({
        t: 'path',
        d,
        stroke: FIGURE_PALETTE[i % 4],
        width: 2.4,
        fill: 'none',
        clip: true,
      })
    }
  })
  spec.functions.slice(0, 4).forEach((f, i) => {
    if (!f.label) return
    const x = domain[1] - (domain[1] - domain[0]) * 0.08
    const y = fns[i](x)
    if (inView(x, y)) {
      items.push(
        text(sx(x) - 4, sy(y) - 10, f.label, {
          anchor: 'end',
          fill: FIGURE_PALETTE[i % 4],
          weight: 700,
        })
      )
    }
  })
  const dot = (x, y, fill) => ({
    t: 'circle',
    cx: sx(x),
    cy: sy(y),
    r: 4.5,
    fill,
    stroke: '#fff',
    width: 1.5,
  })
  if (marks.has('roots')) {
    roots(first, domain).forEach(x => {
      if (!inView(x, 0)) return
      items.push(dot(x, 0, RED))
      items.push(
        text(sx(x), sy(0) + 14, fmt(round(x)), {
          fill: RED,
          weight: 700,
          size: 12,
        })
      )
    })
  }
  if (marks.has('vertex')) {
    extrema(first, domain).forEach(v => {
      if (!inView(v.x, v.y)) return
      items.push(dot(v.x, v.y, TEAL))
      // Below a minimum, above a maximum -- unless that lands on the x-axis
      // tick labels, then on the other side.
      let labelY = sy(v.y) + (v.kind === 'min' ? 15 : -13)
      if (Math.abs(labelY - (frame.axisY + 13)) < 14) {
        labelY = sy(v.y) + (v.kind === 'min' ? -13 : 15)
      }
      items.push(
        text(sx(v.x), labelY, `(${fmt(round(v.x))}, ${fmt(round(v.y))})`, {
          fill: TEAL,
          weight: 700,
          size: 12,
        })
      )
    })
  }
  if (marks.has('y_intercept') && domain[0] <= 0 && domain[1] >= 0) {
    const y = first(0)
    if (Number.isFinite(y) && inView(0, y)) {
      items.push(dot(0, y, AMBER))
      items.push(
        text(sx(0) + 7, sy(y) - 9, fmt(round(y)), {
          anchor: 'start',
          fill: AMBER,
          weight: 700,
          size: 12,
        })
      )
    }
  }
  if (marks.has('intersections') && fns.length > 1) {
    intersections(fns[0], fns[1], domain).forEach(p => {
      if (inView(p.x, p.y)) items.push(dot(p.x, p.y, DEEP))
    })
  }
  ;(Array.isArray(spec.points) ? spec.points : []).forEach(p => {
    if (!finite(p.x) || !finite(p.y) || !inView(p.x, p.y)) return
    items.push(dot(p.x, p.y, INK))
    if (p.label) {
      items.push(
        text(sx(p.x) + 6, sy(p.y) - 9, p.label, { anchor: 'start', size: 12 })
      )
    }
  })
  return {
    type: 'svg',
    width: WIDTH,
    height,
    clip: {
      x: frame.pad.left,
      y: frame.pad.top,
      width: frame.plotW,
      height: frame.plotH,
    },
    items,
  }
}

function numberLine(spec, fmt) {
  if (!finite(spec.min) || !finite(spec.max) || spec.min >= spec.max) {
    throw new Error('bad number line')
  }
  const span = spec.max - spec.min
  const pad = span * 0.05
  const height = 120
  const left = 22
  const right = WIDTH - 22
  const axisY = 66
  const sx = x =>
    left + ((x - (spec.min - pad)) / (span + 2 * pad)) * (right - left)
  const items = [
    {
      t: 'line',
      x1: left,
      y1: axisY,
      x2: right,
      y2: axisY,
      stroke: MUTED,
      width: 1.6,
    },
  ]
  ticks(
    spec.min,
    spec.max,
    Math.max(4, Math.min(10, Math.floor(WIDTH / 50)))
  ).forEach(x => {
    items.push({
      t: 'line',
      x1: sx(x),
      y1: axisY - 5,
      x2: sx(x),
      y2: axisY + 5,
      stroke: MUTED,
      width: 1.2,
    })
    items.push(text(sx(x), axisY + 18, fmt(x), { size: 11, fill: MUTED }))
  })
  ;(Array.isArray(spec.intervals) ? spec.intervals : [])
    .slice(0, 6)
    .forEach((iv, i) => {
      if (!finite(iv.from) || !finite(iv.to)) return
      const color = FIGURE_PALETTE[i % 4]
      items.push({
        t: 'line',
        x1: sx(iv.from),
        y1: axisY,
        x2: sx(iv.to),
        y2: axisY,
        stroke: color,
        width: 6,
        opacity: 0.85,
      })
      const end = (x, closed) => ({
        t: 'circle',
        cx: sx(x),
        cy: axisY,
        r: 6,
        fill: closed ? color : '#fff',
        stroke: color,
        width: 2.4,
      })
      items.push(end(iv.from, !!iv.closedFrom))
      items.push(end(iv.to, !!iv.closedTo))
      if (iv.label) {
        items.push(
          text(
            (sx(iv.from) + sx(iv.to)) / 2,
            axisY - 22 - (i % 2) * 16,
            iv.label,
            { fill: color, weight: 700, size: 12 }
          )
        )
      }
    })
  ;(Array.isArray(spec.points) ? spec.points : []).slice(0, 12).forEach(p => {
    if (!finite(p.value)) return
    items.push({
      t: 'circle',
      cx: sx(p.value),
      cy: axisY,
      r: 5,
      fill: RED,
      stroke: '#fff',
      width: 1.5,
    })
    items.push(
      text(sx(p.value), axisY + 36, p.label || fmt(p.value), {
        fill: RED,
        weight: 700,
        size: 12,
      })
    )
  })
  return { type: 'svg', width: WIDTH, height, items }
}

function triangle(spec, fmt) {
  const given = Array.isArray(spec.angles) ? spec.angles.filter(finite) : []
  if (given.length < 2) throw new Error('two angles needed')
  const [a, b] = given
  const c = given.length > 2 ? given[2] : 180 - a - b
  if (!(a > 0 && b > 0 && c > 0) || Math.abs(a + b + c - 180) > 0.01) {
    throw new Error('not a triangle')
  }
  const rad = d => (d * Math.PI) / 180
  // B at the origin, C on the x axis (as the reference console lays it out);
  // A from the angle at B and |AB| by the law of sines, which also holds for
  // a right angle at B or C.
  const base = 6
  const B = [0, 0]
  const C = [base, 0]
  const ab = (base * Math.sin(rad(c))) / Math.sin(rad(a))
  const A = [ab * Math.cos(rad(b)), ab * Math.sin(rad(b))]
  const vertices = [A, B, C]
  const angles = [a, b, c]
  const xs = vertices.map(v => v[0])
  const ys = vertices.map(v => v[1])
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const height = 280
  const margin = 44
  const scale = Math.min(
    (WIDTH - 2 * margin) / (maxX - minX || 1),
    (height - 2 * margin) / (maxY - minY || 1)
  )
  const offsetX = (WIDTH - (maxX - minX) * scale) / 2
  const offsetY = (height - (maxY - minY) * scale) / 2
  const px = p => [
    offsetX + (p[0] - minX) * scale,
    height - offsetY - (p[1] - minY) * scale,
  ]
  const P = vertices.map(px)
  const items = [
    {
      t: 'polygon',
      points: P.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' '),
      stroke: FIGURE_PALETTE[0],
      fill: FIGURE_PALETTE[0],
      fillOpacity: 0.08,
      width: 2.2,
    },
  ]
  const centroid = [
    (P[0][0] + P[1][0] + P[2][0]) / 3,
    (P[0][1] + P[1][1] + P[2][1]) / 3,
  ]
  const outward = (point, distance) => {
    const dx = point[0] - centroid[0]
    const dy = point[1] - centroid[1]
    const len = Math.hypot(dx, dy) || 1
    return [point[0] + (dx / len) * distance, point[1] + (dy / len) * distance]
  }
  const names =
    Array.isArray(spec.vertices) && spec.vertices.length === 3
      ? spec.vertices
      : ['A', 'B', 'C']
  P.forEach((p, i) => {
    const at = outward(p, 16)
    items.push(text(at[0], at[1], names[i], { weight: 700 }))
  })
  if (spec.showAngles !== false) {
    const radius = 26
    P.forEach((p, i) => {
      const others = P.filter((_, j) => j !== i)
      const dirs = others.map(o => Math.atan2(o[1] - p[1], o[0] - p[0]))
      let start = dirs[0]
      let delta = dirs[1] - dirs[0]
      while (delta > Math.PI) delta -= 2 * Math.PI
      while (delta < -Math.PI) delta += 2 * Math.PI
      const end = start + delta
      if (Math.abs(angles[i] - 90) < 0.01) {
        const r = 12
        const u = [Math.cos(start), Math.sin(start)]
        const v = [Math.cos(end), Math.sin(end)]
        items.push({
          t: 'path',
          d: `M${p[0] + u[0] * r} ${p[1] + u[1] * r}L${p[0] +
            (u[0] + v[0]) * r} ${p[1] + (u[1] + v[1]) * r}L${p[0] +
            v[0] * r} ${p[1] + v[1] * r}`,
          stroke: RED,
          width: 1.6,
          fill: 'none',
        })
      } else {
        const sweep = delta > 0 ? 1 : 0
        items.push({
          t: 'path',
          d: `M${(p[0] + Math.cos(start) * radius).toFixed(1)} ${(
            p[1] +
            Math.sin(start) * radius
          ).toFixed(1)}A${radius} ${radius} 0 0 ${sweep} ${(
            p[0] +
            Math.cos(end) * radius
          ).toFixed(1)} ${(p[1] + Math.sin(end) * radius).toFixed(1)}`,
          stroke: RED,
          width: 1.6,
          fill: 'none',
        })
      }
      const mid = start + delta / 2
      items.push(
        text(
          p[0] + Math.cos(mid) * (radius + 16),
          p[1] + Math.sin(mid) * (radius + 16),
          `${fmt(round(angles[i], 1))}°`,
          {
            size: 12,
            fill: RED,
            weight: 700,
          }
        )
      )
    })
  }
  if (Array.isArray(spec.sides) && spec.sides.length === 3) {
    spec.sides.forEach((label, i) => {
      if (!label) return
      const [p, q] = P.filter((_, j) => j !== i)
      const at = outward([(p[0] + q[0]) / 2, (p[1] + q[1]) / 2], 14)
      items.push(
        text(at[0], at[1], label, { size: 13, fill: DEEP, weight: 700 })
      )
    })
  }
  return { type: 'svg', width: WIDTH, height, items }
}

function unitCircle(spec, fmt) {
  const angles = Array.isArray(spec.angles)
    ? spec.angles.filter(finite).slice(0, 4)
    : []
  if (!angles.length) throw new Error('no angles')
  const show = new Set(Array.isArray(spec.show) ? spec.show : ['sin', 'cos'])
  const size = Math.min(320, WIDTH)
  const view = 1.6
  const s = v => ((v + view) / (2 * view)) * size
  const sy = v => size - s(v)
  const items = [
    {
      t: 'line',
      x1: s(-view),
      y1: sy(0),
      x2: s(view),
      y2: sy(0),
      stroke: MUTED,
      width: 1.2,
    },
    {
      t: 'line',
      x1: s(0),
      y1: sy(-view),
      x2: s(0),
      y2: sy(view),
      stroke: MUTED,
      width: 1.2,
    },
    {
      t: 'circle',
      cx: s(0),
      cy: sy(0),
      r: s(1) - s(0),
      fill: 'none',
      stroke: INK,
      width: 1.6,
    },
    text(s(1) + 6, sy(0) + 12, fmt(1), {
      size: 11,
      fill: MUTED,
      anchor: 'start',
    }),
    text(s(0) - 6, sy(1) - 8, fmt(1), { size: 11, fill: MUTED, anchor: 'end' }),
  ]
  angles.forEach((deg, i) => {
    const t = (deg * Math.PI) / 180
    const p = [Math.cos(t), Math.sin(t)]
    const color = FIGURE_PALETTE[i % 4]
    if (show.has('cos')) {
      items.push({
        t: 'line',
        x1: s(0),
        y1: sy(0),
        x2: s(p[0]),
        y2: sy(0),
        stroke: TEAL,
        width: 3,
      })
      items.push(
        text(
          s(p[0] / 2),
          sy(0) + (p[1] >= 0 ? 14 : -12),
          `cos = ${fmt(round(p[0]))}`,
          { size: 11, fill: TEAL, weight: 700 }
        )
      )
    }
    if (show.has('sin')) {
      items.push({
        t: 'line',
        x1: s(p[0]),
        y1: sy(0),
        x2: s(p[0]),
        y2: sy(p[1]),
        stroke: RED,
        width: 2,
      })
      items.push(
        text(
          s(p[0]) + (p[0] >= 0 ? 6 : -6),
          sy(p[1] / 2),
          `sin = ${fmt(round(p[1]))}`,
          {
            size: 11,
            fill: RED,
            weight: 700,
            anchor: p[0] >= 0 ? 'start' : 'end',
          }
        )
      )
    }
    if (show.has('tan') && Math.abs(Math.cos(t)) > 1e-6) {
      const tan = Math.max(-view, Math.min(view, Math.tan(t)))
      items.push({
        t: 'line',
        x1: s(1),
        y1: sy(0),
        x2: s(1),
        y2: sy(tan),
        stroke: AMBER,
        width: 2,
      })
    }
    items.push({
      t: 'line',
      x1: s(0),
      y1: sy(0),
      x2: s(p[0]),
      y2: sy(p[1]),
      stroke: color,
      width: 2.4,
    })
    const r = 0.28
    const arc = []
    const steps = Math.max(2, Math.ceil(Math.abs(deg) / 6))
    for (let k = 0; k <= steps; k += 1) {
      const u = (t * k) / steps
      arc.push(
        `${k ? 'L' : 'M'}${s(r * Math.cos(u)).toFixed(1)} ${sy(
          r * Math.sin(u)
        ).toFixed(1)}`
      )
    }
    items.push({
      t: 'path',
      d: arc.join(''),
      stroke: color,
      width: 1.6,
      fill: 'none',
    })
    items.push(
      text(
        s(0.46 * Math.cos(t / 2)),
        sy(0.46 * Math.sin(t / 2)),
        `${fmt(deg)}°`,
        { size: 12, fill: color, weight: 700 }
      )
    )
    items.push({
      t: 'circle',
      cx: s(p[0]),
      cy: sy(p[1]),
      r: 4.5,
      fill: color,
      stroke: '#fff',
      width: 1.5,
    })
  })
  return { type: 'svg', width: size, height: size, items }
}

function signTableModel(spec, fmt) {
  const factors = Array.isArray(spec.factors) ? spec.factors.slice(0, 4) : []
  if (!factors.length) throw new Error('no factors')
  const domain = pair(spec.domain, [-50, 50])
  const table = signTable(factors, domain)
  const fns = factors.map(toFunction)
  return {
    type: 'sign_table',
    cuts: table.cuts.map(cut => fmt(round(cut))),
    // Per factor: its sign on each interval and whether it is zero at each
    // cut (a cut is some factor's root, not necessarily this one's).
    rows: table.rows.map((row, i) => ({
      label: row.label,
      signs: row.signs,
      zeros: table.cuts.map(cut => Math.abs(fns[i](cut)) < 1e-6),
    })),
    product: table.product,
    single: factors.length === 1,
  }
}

function venn(spec) {
  const sets = Array.isArray(spec.sets) ? spec.sets.slice(0, 3) : []
  if (sets.length < 2) throw new Error('two sets needed')
  const three = sets.length === 3
  const height = three ? 330 : 270
  const cx = WIDTH / 2
  // Narrow figures shrink the circles, not the text.
  const k = Math.min(1, WIDTH / 400)
  const r = (three ? 92 : 100) * k
  const centers = three
    ? [
        [cx - 58 * k, 130],
        [cx + 58 * k, 130],
        [cx, 130 + 84 * k],
      ]
    : [
        [cx - 62 * k, 135],
        [cx + 62 * k, 135],
      ]
  const items = []
  sets.forEach((set, i) => {
    items.push({
      t: 'circle',
      cx: centers[i][0],
      cy: centers[i][1],
      r,
      fill: FIGURE_PALETTE[i],
      fillOpacity: 0.1,
      stroke: FIGURE_PALETTE[i],
      width: 2,
    })
    const nameAt = three
      ? [
          [centers[0][0] - r * 0.78, centers[0][1] - r * 0.8],
          [centers[1][0] + r * 0.78, centers[1][1] - r * 0.8],
          [centers[2][0], centers[2][1] + r + 16],
        ][i]
      : [centers[i][0] + (i ? 1 : -1) * r * 0.78, centers[i][1] - r * 0.86]
    items.push(
      text(nameAt[0], nameAt[1], set.name, {
        size: 16,
        fill: FIGURE_PALETTE[i],
        weight: 700,
      })
    )
  })
  const has = (set, element) =>
    !!set && Array.isArray(set.elements) && set.elements.includes(element)
  const all = []
  const push = element => {
    if (typeof element === 'string' && element && !all.includes(element))
      all.push(element)
  }
  ;(Array.isArray(spec.universe) ? spec.universe : []).forEach(push)
  sets.forEach(set =>
    (Array.isArray(set.elements) ? set.elements : []).forEach(push)
  )
  const groups = new Map()
  all.forEach(element => {
    const inside = sets
      .map((set, i) => (has(set, element) ? i : -1))
      .filter(i => i >= 0)
    const key = inside.length ? inside.join('') : 'out'
    groups.set(key, [...(groups.get(key) || []), element])
  })
  groups.forEach((elements, key) => {
    let at
    if (key === 'out') {
      at = [WIDTH - 60, height - 18]
    } else {
      const members = key.split('').map(Number)
      at = members.reduce(
        (acc, i) => [
          acc[0] + centers[i][0] / members.length,
          acc[1] + centers[i][1] / members.length,
        ],
        [0, 0]
      )
      if (members.length === 1) {
        // Only this set: push away from the others' middle.
        const others = centers.filter((_, j) => j !== members[0])
        const mid = others.reduce(
          (acc, c) => [
            acc[0] + c[0] / others.length,
            acc[1] + c[1] / others.length,
          ],
          [0, 0]
        )
        const dx = at[0] - mid[0]
        const dy = at[1] - mid[1]
        const len = Math.hypot(dx, dy) || 1
        at = [at[0] + (dx / len) * r * 0.42, at[1] + (dy / len) * r * 0.42]
      }
    }
    // Long groups wrap onto a few lines of up to four elements.
    for (let line = 0; line * 4 < elements.length && line < 4; line += 1) {
      const lines = Math.min(4, Math.ceil(elements.length / 4))
      items.push(
        text(
          at[0],
          at[1] + (line - (lines - 1) / 2) * 16,
          elements.slice(line * 4, line * 4 + 4).join('، '),
          { size: 13 }
        )
      )
    }
  })
  return { type: 'svg', width: WIDTH, height, items }
}

function tree(spec, fmt, labels) {
  const levels = Array.isArray(spec.levels) ? spec.levels.slice(0, 4) : []
  if (
    !levels.length ||
    levels.some(l => !Array.isArray(l.options) || !l.options.length)
  ) {
    throw new Error('empty tree')
  }
  const leaves = levels.reduce((n, level) => n * level.options.length, 1)
  if (leaves > 216) throw new Error('tree too large')
  const rowGap = 64
  const height = 40 + rowGap * levels.length + 20
  let layer = [{ x: WIDTH / 2, y: 26, label: labels.treeStart }]
  const nodes = [...layer]
  const edges = []
  levels.forEach((level, depth) => {
    const next = []
    const count = layer.length * level.options.length
    layer.forEach((parent, pi) =>
      level.options.forEach((option, oi) => {
        const index = pi * level.options.length + oi
        const node = {
          x: 16 + ((index + 0.5) * (WIDTH - 32)) / count,
          y: 26 + rowGap * (depth + 1),
          label: String(option),
        }
        edges.push({
          t: 'line',
          x1: parent.x,
          y1: parent.y,
          x2: node.x,
          y2: node.y,
          stroke: MUTED,
          width: 1.2,
          opacity: 0.55,
        })
        next.push(node)
      })
    )
    nodes.push(...next)
    layer = next
  })
  const leafWidth = (WIDTH - 32) / leaves
  const items = [...edges]
  nodes.forEach((node, i) => {
    items.push({
      t: 'circle',
      cx: node.x,
      cy: node.y,
      r: 4.5,
      fill: i ? FIGURE_PALETTE[0] : INK,
      stroke: '#fff',
      width: 1.5,
    })
    // Leaves too narrow for their label skip it rather than overlap.
    const isLeaf = node.y === 26 + rowGap * levels.length
    if (!isLeaf || leafWidth >= 26) {
      items.push(
        text(node.x, node.y + 15, node.label, {
          size: isLeaf && leafWidth < 48 ? 10 : 12,
        })
      )
    }
  })
  levels.forEach((level, depth) => {
    if (level.label) {
      items.push(
        text(4, 26 + rowGap * (depth + 1) - 16, level.label, {
          anchor: 'start',
          size: 11,
          fill: MUTED,
          weight: 700,
        })
      )
    }
  })
  return {
    type: 'svg',
    width: WIDTH,
    height,
    items,
    footer: `${levels.map(l => fmt(l.options.length)).join(' × ')} = ${fmt(
      leaves
    )} ${labels.treeCases}`,
  }
}

function barChart(spec, fmt) {
  const series = Array.isArray(spec.series)
    ? spec.series.filter(s => s && finite(s.value)).slice(0, 12)
    : []
  if (!series.length) throw new Error('empty series')
  const values = series.map(s => s.value)
  const min = Math.min(0, ...values)
  const max = Math.max(0, ...values)
  const span = max - min || 1
  const height = 280
  const pad = { left: 38, right: 12, top: spec.title ? 34 : 18, bottom: 44 }
  const plotW = WIDTH - pad.left - pad.right
  const plotH = height - pad.top - pad.bottom
  const sy = v => pad.top + plotH - ((v - min) / span) * plotH
  const items = []
  if (spec.title)
    items.push(text(WIDTH / 2, 14, spec.title, { weight: 700, size: 13 }))
  ticks(min, max, 5).forEach(v => {
    items.push({
      t: 'line',
      x1: pad.left,
      y1: sy(v),
      x2: pad.left + plotW,
      y2: sy(v),
      stroke: GRID,
      width: 1,
    })
    items.push(
      text(pad.left - 6, sy(v), fmt(v), {
        anchor: 'end',
        size: 11,
        fill: MUTED,
      })
    )
  })
  items.push({
    t: 'line',
    x1: pad.left,
    y1: sy(0),
    x2: pad.left + plotW,
    y2: sy(0),
    stroke: MUTED,
    width: 1.4,
  })
  const slot = plotW / series.length
  const barW = Math.min(56, slot * 0.62)
  series.forEach((s, i) => {
    const x = pad.left + slot * i + (slot - barW) / 2
    const top = Math.min(sy(s.value), sy(0))
    items.push({
      t: 'rect',
      x,
      y: top,
      w: barW,
      h: Math.max(1, Math.abs(sy(s.value) - sy(0))),
      fill: FIGURE_PALETTE[i % FIGURE_PALETTE.length],
      rx: 4,
    })
    items.push(
      text(
        x + barW / 2,
        s.value >= 0 ? top - 8 : top + Math.abs(sy(s.value) - sy(0)) + 10,
        fmt(round(s.value)),
        { size: 11, weight: 700 }
      )
    )
    items.push(
      text(
        x + barW / 2,
        height - pad.bottom + 16,
        String(s.label).slice(0, Math.max(3, Math.floor(slot / 7))),
        { size: series.length > 8 ? 10 : 12 }
      )
    )
  })
  return { type: 'svg', width: WIDTH, height, items }
}

function pieChart(spec, fmt) {
  const series = Array.isArray(spec.series)
    ? spec.series.filter(s => s && finite(s.value) && s.value > 0).slice(0, 12)
    : []
  const total = series.reduce((sum, s) => sum + s.value, 0)
  if (!series.length || !(total > 0)) throw new Error('empty pie')
  // Legend beside the pie, or under it when the figure is narrow.
  const stacked = WIDTH < 480
  const r = stacked ? Math.min(100, WIDTH / 2 - 24) : 100
  const cx = stacked ? WIDTH / 2 : 150
  const cy = (spec.title ? 32 : 18) + r + 12
  const legendX = stacked ? 24 : 300
  const legendTop = stacked ? cy + r + 26 : cy - (series.length - 1) * 10
  const height = stacked
    ? legendTop + series.length * 20 + 6
    : Math.max(260, cy + r + 18)
  const items = []
  if (spec.title)
    items.push(text(WIDTH / 2, 14, spec.title, { weight: 700, size: 13 }))
  let angle = -Math.PI / 2
  series.forEach((s, i) => {
    const color = FIGURE_PALETTE[i % FIGURE_PALETTE.length]
    const sweep = (s.value / total) * Math.PI * 2
    if (series.length === 1) {
      items.push({
        t: 'circle',
        cx,
        cy,
        r,
        fill: color,
        stroke: '#fff',
        width: 2,
      })
    } else {
      const end = angle + sweep
      const large = sweep > Math.PI ? 1 : 0
      items.push({
        t: 'path',
        d: `M${cx} ${cy}L${(cx + r * Math.cos(angle)).toFixed(1)} ${(
          cy +
          r * Math.sin(angle)
        ).toFixed(1)}A${r} ${r} 0 ${large} 1 ${(cx + r * Math.cos(end)).toFixed(
          1
        )} ${(cy + r * Math.sin(end)).toFixed(1)}Z`,
        fill: color,
        stroke: '#fff',
        width: 2,
      })
    }
    angle += sweep
    const rowY = legendTop + i * 20
    items.push({
      t: 'rect',
      x: legendX,
      y: rowY - 6,
      w: 12,
      h: 12,
      fill: color,
      rx: 3,
    })
    items.push(
      text(
        legendX + 20,
        rowY,
        `${String(s.label).slice(0, 18)} · ${fmt(
          round((s.value / total) * 100, 1)
        )}٪`,
        {
          anchor: 'start',
          size: 12,
        }
      )
    )
  })
  return { type: 'svg', width: WIDTH, height, items }
}

/**
 * @param {Object} spec -- one validated upstream figure spec
 * @param {{persian?: boolean, width?: number,
 *   labels: {treeStart:string, treeCases:string}}} options -- `width`: the
 *   px width available on the page (560 when unknown)
 * @returns {Object} {type:'svg', width, height, items, clip?, footer?}
 *   | {type:'sign_table', cuts, rows, product, single}
 *   | {type:'image', url, caption}
 */
export function layoutFigure(spec, options) {
  if (!spec || typeof spec !== 'object') throw new Error('no figure')
  const fmt = value => formatNumber(value, options.persian !== false)
  const width = Number(options.width)
  WIDTH =
    width > 0
      ? Math.round(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width)))
      : MAX_WIDTH
  switch (spec.kind) {
    case 'function_plot':
      return functionPlot(spec, fmt)
    case 'number_line':
      return numberLine(spec, fmt)
    case 'geometry':
      return spec.shape === 'unit_circle'
        ? unitCircle(spec, fmt)
        : triangle(spec, fmt)
    case 'sign_table':
      return signTableModel(spec, fmt)
    case 'venn':
      return venn(spec)
    case 'tree':
      return tree(spec, fmt, options.labels)
    case 'bar':
      return barChart(spec, fmt)
    case 'pie':
      return pieChart(spec, fmt)
    case 'book_figure':
      return {
        type: 'image',
        url: typeof spec.imageUrl === 'string' ? spec.imageUrl : '',
        caption: spec.caption || '',
      }
    default:
      throw new Error(`unknown figure kind ${spec.kind}`)
  }
}
